import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { OnboardingStatus } from '../../../hooks/coach/useOnboardingChecklist';

export interface OnboardingChecklistProps {
  status: OnboardingStatus;
  onDismiss: () => void;
  /**
   * Hook opcional que el parent puede usar para interceptar el click en una
   * tarea (por ejemplo, abrir un modal). Si devuelve `true`, el checklist no
   * navega; cualquier otro retorno (incluyendo `void`) deja que el navigate
   * por default ocurra. De esta forma el parent puede manejar solo las tareas
   * que le interesan y las demás siguen funcionando.
   */
  // eslint-disable-next-line no-unused-vars
  onTaskClick?: (taskId: TaskId) => boolean | void;
}

export type TaskId = 'profile' | 'library' | 'template' | 'cycle' | 'client' | 'assignPlan';

interface TaskDefinition {
  id: TaskId;
  done: boolean;
  labelKey: string;
  defaultLabel: string;
  ctaKey: string;
  defaultCta: string;
  navigateTo?: string;
}

const buildTasks = (status: OnboardingStatus): TaskDefinition[] => [
  {
    id: 'profile',
    done: status.profileComplete,
    labelKey: 'coach.onboarding.task.profile',
    defaultLabel: 'Completá tu perfil',
    ctaKey: 'coach.onboarding.task.profile.cta',
    defaultCta: 'Completar perfil',
    navigateTo: '/coach/profile'
  },
  {
    id: 'library',
    done: status.libraryViewed,
    labelKey: 'coach.onboarding.task.library',
    defaultLabel: 'Explorá la biblioteca de ejercicios',
    ctaKey: 'coach.onboarding.task.library.cta',
    defaultCta: 'Ver biblioteca',
    // El tab "Ejercicios" vive en /coach/profile (CoachProfile TabView, índice 0).
    navigateTo: '/coach/profile?tab=exercises'
  },
  {
    id: 'template',
    done: status.hasTemplate,
    labelKey: 'coach.onboarding.task.template',
    defaultLabel: 'Creá tu primera plantilla de sesión',
    ctaKey: 'coach.onboarding.task.template.cta',
    defaultCta: 'Crear plantilla',
    // /coach/plans → tab "Entrenamientos" (workouts) por default; el botón "Crear" lleva a /plans/create.
    navigateTo: '/coach/plans?tab=sessions'
  },
  {
    id: 'cycle',
    done: status.hasCycle,
    labelKey: 'coach.onboarding.task.cycle',
    defaultLabel: 'Creá tu primer ciclo de entrenamiento',
    ctaKey: 'coach.onboarding.task.cycle.cta',
    defaultCta: 'Crear ciclo',
    // /coach/plans → forzamos abrir directamente en el tab "Ciclos".
    navigateTo: '/coach/plans?tab=cycles'
  },
  {
    id: 'client',
    done: status.hasClient,
    labelKey: 'coach.onboarding.task.client',
    defaultLabel: 'Invitá tu primer alumno',
    ctaKey: 'coach.onboarding.task.client.cta',
    defaultCta: 'Invitar alumno',
    navigateTo: '/manage-students'
  },
  {
    id: 'assignPlan',
    done: status.hasAssignedPlan,
    labelKey: 'coach.onboarding.task.assignPlan',
    defaultLabel: 'Asignale un plan a tu alumno',
    ctaKey: 'coach.onboarding.task.assignPlan.cta',
    defaultCta: 'Asignar plan',
    navigateTo: '/manage-students'
  }
];

export function OnboardingChecklist({ status, onDismiss, onTaskClick }: OnboardingChecklistProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const tasks = buildTasks(status);
  const percent = Math.round((status.completedCount / status.totalCount) * 100);

  const handleTaskClick = (task: TaskDefinition): void => {
    if (task.done) return;
    // El parent puede manejar tareas específicas devolviendo `true` para indicar
    // "ya me encargué" (ej. abrir un modal). Cualquier otro retorno cae al
    // navigate por default — así no se rompen las tareas que el parent no maneja.
    const handled = onTaskClick?.(task.id) === true;
    if (handled) return;
    if (task.navigateTo) {
      navigate(task.navigateTo);
    }
  };

  return (
    <section
      data-testid="onboarding-checklist"
      style={{
        background: 'var(--ios-card-bg)',
        border: '1px solid var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-lg)',
        boxShadow: 'var(--ios-card-shadow)',
        padding: '1.25rem',
        marginBottom: '1rem'
      }}
    >
      <header className="flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h2
            className="m-0"
            style={{
              fontSize: '1.05rem',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'var(--ios-text)'
            }}
          >
            {intl.formatMessage({
              id: 'coach.onboarding.title',
              defaultMessage: '👋 Te ayudamos a empezar'
            })}
          </h2>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '0.85rem',
              color: 'var(--ios-text-secondary)'
            }}
          >
            {intl.formatMessage(
              {
                id: 'coach.onboarding.progress',
                defaultMessage: 'Llevás {done} de {total} pasos completados'
              },
              { done: status.completedCount, total: status.totalCount }
            )}
          </p>
        </div>
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-rounded p-button-sm"
          onClick={onDismiss}
          tooltip={intl.formatMessage({
            id: 'coach.onboarding.dismiss',
            defaultMessage: 'No mostrar más'
          })}
          tooltipOptions={{ position: 'left' }}
        />
      </header>

      <div
        style={{
          height: 6,
          width: '100%',
          background: 'var(--ios-surface-muted, rgba(0,0,0,0.06))',
          borderRadius: 9999,
          overflow: 'hidden',
          marginBottom: '1rem'
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            background: '#a3e635',
            borderRadius: 9999,
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {tasks.map((task) => (
          <li
            key={task.id}
            data-testid={`onboarding-task-${task.id}`}
            data-done={task.done}
            className="flex align-items-center gap-3"
            style={{
              padding: '0.55rem 0',
              borderTop: '1px solid var(--ios-divider, rgba(0,0,0,0.06))'
            }}
          >
            <span
              aria-hidden
              style={{
                width: 22,
                height: 22,
                borderRadius: 9999,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: task.done ? '#22c55e' : 'transparent',
                border: task.done ? 'none' : '1.5px solid var(--ios-divider)',
                color: '#fff',
                flexShrink: 0
              }}
            >
              {task.done && <i className="pi pi-check" style={{ fontSize: 12 }} />}
            </span>
            <span
              style={{
                flex: 1,
                fontSize: '0.92rem',
                color: task.done ? 'var(--ios-text-tertiary)' : 'var(--ios-text)',
                textDecoration: task.done ? 'line-through' : 'none'
              }}
            >
              {intl.formatMessage({
                id: task.labelKey,
                defaultMessage: task.defaultLabel
              })}
            </span>
            {!task.done && (
              <Button
                label={intl.formatMessage({
                  id: task.ctaKey,
                  defaultMessage: task.defaultCta
                })}
                className="p-button-outlined p-button-sm"
                onClick={() => handleTaskClick(task)}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
